interface TermsContentProps {
  compact?: boolean;
}

export const TermsContent = ({ compact = false }: TermsContentProps) => {
  const headingClass = compact 
    ? "text-base font-semibold mb-2" 
    : "text-lg font-semibold mb-3";
  const textClass = compact
    ? "text-muted-foreground leading-relaxed text-sm"
    : "text-muted-foreground leading-relaxed";
  const listClass = compact
    ? "list-disc list-inside space-y-1 text-muted-foreground text-sm ml-4"
    : "list-disc list-inside space-y-2 text-muted-foreground ml-4";

  return (
    <div className="space-y-6">
      <div>
        <p className={`${textClass} font-medium`}>Last updated: June 2025</p>
      </div>

      <p className={textClass}>
        These Terms and Conditions ("Terms") govern your use of the Straiv mobile application ("Straiv", "we", "us", "the app").
        By creating an account or using the app, you agree to these Terms.
      </p>

      <section>
        <h3 className={headingClass}>1. Scope of Service</h3>
        <p className={`${textClass} mb-2`}>
          Straiv provides digital workout routines, habit tracking, nutritional guidance, and AI-driven coaching through our assistant.
        </p>
        <p className={`${textClass} mb-2`}>
          All information provided through Straiv is for informational and educational purposes only. It is not medical advice, diagnosis, or treatment.
        </p>
        <p className={textClass}>
          Always consult a healthcare professional before beginning any fitness, nutrition, or wellness program.
        </p>
      </section>

      <section>
        <h3 className={headingClass}>2. Eligibility</h3>
        <ul className={listClass}>
          <li>You must be at least 16 years old to use Straiv.</li>
          <li>If you are under 18, you must have parental consent.</li>
          <li>Straiv is not intended for children under 13.</li>
        </ul>
      </section>

      <section>
        <h3 className={headingClass}>3. User Account and Security</h3>
        <p className={`${textClass} mb-2`}>
          To use Straiv, you must create an account. You agree to:
        </p>
        <ul className={listClass}>
          <li>Provide accurate information</li>
          <li>Keep your login details secure</li>
          <li>Be responsible for all actions under your account</li>
          <li>Notify us immediately if unauthorized access occurs</li>
        </ul>
        <p className={`${textClass} mt-2`}>
          We may suspend or terminate accounts that violate these Terms.
        </p>
      </section>

      <section>
        <h3 className={headingClass}>4. Acceptable Use & User Conduct</h3>
        <p className={`${textClass} mb-2`}>
          You agree not to:
        </p>
        <ul className={listClass}>
          <li>Use Straiv for illegal activities</li>
          <li>Attempt to hack, reverse engineer, or disrupt the service</li>
          <li>Upload harmful, offensive, or abusive content</li>
          <li>Attempt to access other users' data</li>
          <li>Use the service to generate spam or harmful content through AI tools</li>
        </ul>
        <p className={`${textClass} mt-2`}>
          Misuse may result in account suspension or deletion.
        </p>
      </section>

      <section>
        <h3 className={headingClass}>5. Intellectual Property</h3>
        <p className={textClass}>
          All content within Straiv—including text, graphics, images, logos, design, AI prompts, and algorithms—belongs to Straiv.
          You may not copy, modify, distribute, or use any part of the service without our written permission.
        </p>
      </section>

      <section>
        <h3 className={headingClass}>6. AI Disclaimer</h3>
        <p className={`${textClass} mb-2`}>
          Straiv uses AI technology powered by Google Gemini to provide recommendations. You understand and agree that:
        </p>
        <ul className={listClass}>
          <li>AI responses may be inaccurate or incomplete</li>
          <li>AI does not replace certified medical or fitness professionals</li>
          <li>You should verify advice with reliable sources or professionals</li>
          <li>You use AI-generated insights at your own risk</li>
        </ul>
        <p className={`${textClass} mt-2`}>
          We explicitly disclaim responsibility for any decisions you make based on AI responses.
        </p>
      </section>

      <section>
        <h3 className={headingClass}>7. Health & Safety Disclaimer</h3>
        <p className={`${textClass} mb-2`}>
          Straiv is not a medical service. All fitness and nutritional guidance is general and may not be suitable for every user.
        </p>
        <p className={`${textClass} mb-2`}>
          Stop immediately and consult a doctor if you experience:
        </p>
        <ul className={listClass}>
          <li>Pain</li>
          <li>Dizziness</li>
          <li>Shortness of breath</li>
          <li>Any injury or health concern</li>
        </ul>
        <p className={`${textClass} mt-2`}>
          By using Straiv, you accept full responsibility for your health and exercise decisions.
        </p>
      </section>

      <section>
        <h3 className={headingClass}>8. Limitation of Liability</h3>
        <p className={`${textClass} mb-2`}>
          To the maximum extent permitted by law:
        </p>
        <ul className={listClass}>
          <li>Straiv is provided "as is" and "as available"</li>
          <li>We do not guarantee error-free or uninterrupted service</li>
          <li>We are not liable for any damages, injuries, losses, or health outcomes resulting from app usage</li>
          <li>We are not responsible for actions based on AI-generated content</li>
          <li>We are not liable for third-party service failures (Supabase, Google Gemini, Apple, Google Sign-In)</li>
        </ul>
        <p className={`${textClass} mt-2`}>
          Your only remedy if you are dissatisfied with the app is to stop using it.
        </p>
      </section>

      <section>
        <h3 className={headingClass}>9. Modifications to the Service</h3>
        <p className={`${textClass} mb-2`}>
          We may modify, update, or discontinue features at any time, including:
        </p>
        <ul className={listClass}>
          <li>Workout programs</li>
          <li>AI models</li>
          <li>Nutrition features</li>
          <li>App layout</li>
          <li>Supported devices</li>
        </ul>
        <p className={`${textClass} mt-2`}>
          We are not required to maintain any specific feature indefinitely.
        </p>
      </section>

      <section>
        <h3 className={headingClass}>10. Termination</h3>
        <p className={`${textClass} mb-2`}>
          We may suspend or terminate your account if you:
        </p>
        <ul className={listClass}>
          <li>Violate these Terms</li>
          <li>Abuse the AI system</li>
          <li>Attempt to circumvent usage limits</li>
          <li>Engage in harmful behavior</li>
        </ul>
        <p className={`${textClass} mt-2`}>
          You may delete your account at any time.
        </p>
      </section>

      <section>
        <h3 className={headingClass}>11. Privacy</h3>
        <p className={textClass}>
          Your use of Straiv is governed by our Privacy Policy, which explains how we collect, process, and protect your data.
        </p>
      </section>

      <section>
        <h3 className={headingClass}>12. Data Storage & Third-Party Processing</h3>
        <p className={`${textClass} mb-2`}>
          Straiv uses:
        </p>
        <ul className={listClass}>
          <li>Supabase (database & authentication)</li>
          <li>Google Gemini (AI processing)</li>
          <li>Google / Apple (optional login)</li>
          <li>Analytics providers like Firebase (if enabled)</li>
        </ul>
        <p className={`${textClass} mt-2`}>
          By using Straiv, you consent to this data processing.
        </p>
      </section>

      <section>
        <h3 className={headingClass}>13. Governing Law</h3>
        <p className={`${textClass} mb-2`}>
          These Terms are governed by the laws of Sweden, unless otherwise required by your local consumer rights regulations.
        </p>
        <p className={textClass}>
          Any disputes shall be handled in Swedish courts unless local law provides otherwise.
        </p>
      </section>

      <section>
        <h3 className={headingClass}>14. Changes to These Terms</h3>
        <p className={`${textClass} mb-2`}>
          We may update these Terms from time to time.
        </p>
        <p className={`${textClass} mb-2`}>
          If changes are significant, we will notify users within the app or via email.
        </p>
        <p className={textClass}>
          Continued use of Straiv after changes constitutes acceptance.
        </p>
      </section>

      <section>
        <h3 className={headingClass}>15. Contact Us</h3>
        <p className={`${textClass} mb-2`}>
          For support or questions about these Terms:
        </p>
        <p className={`${textClass} mb-2`}>
          <a href="mailto:circletester123@gmail.com" className="text-primary hover:underline">
            circletester123@gmail.com
          </a>
        </p>
        <p className={textClass}>
          We respond within 48 hours.
        </p>
      </section>
    </div>
  );
};
